import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Car, Sofa, Caravan, Package, ChevronRight, MapPin } from "lucide-react";
import SaveSearchButton from "@/components/marketplace/SaveSearchButton";
import ListingCard3D from "@/components/premium/ListingCard3D";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Type configs share the same theming as MarketplacePage / InventoryDetailPage
const TYPE_TABS = [
  { key: "real-estate",  label: "Properties", icon: Home,    color: "#10B981",
    detailPath: "real-estate", emptyCta: "No properties listed yet" },
  { key: "vehicles",     label: "Vehicles",   icon: Car,     color: "#7C3AED",
    detailPath: "vehicles",    emptyCta: "No vehicles listed yet" },
  { key: "rvs",          label: "RVs",        icon: Caravan, color: "#F59E0B",
    detailPath: "rvs",         emptyCta: "No RVs listed yet" },
  { key: "furniture",    label: "Furniture",  icon: Sofa,    color: "#EC4899",
    detailPath: "furniture",   emptyCta: "No furniture listed yet" },
];

// Also accept the alt key "real_estate" coming from older seed records
const TYPE_ALIASES = { "real_estate": "real-estate", "properties": "real-estate" };

function formatPrice(p) {
  if (p === null || p === undefined || p === "") return "Contact for price";
  const n = Number(p);
  if (!Number.isFinite(n)) return "Contact for price";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function ItemCard({ item, tab }) {
  const Icon = tab.icon;
  const img = item.images?.[0] || item.image_url;
  const subtitleParts = [];
  if (tab.key === "real-estate") {
    if (item.bedrooms) subtitleParts.push(`${item.bedrooms} bd`);
    if (item.bathrooms) subtitleParts.push(`${item.bathrooms} ba`);
    if (item.sqft) subtitleParts.push(`${Number(item.sqft).toLocaleString()} sqft`);
  } else if (tab.key === "vehicles") {
    if (item.year) subtitleParts.push(item.year);
    if (item.make) subtitleParts.push(item.make);
    if (item.model) subtitleParts.push(item.model);
    if (item.mileage) subtitleParts.push(`${Number(item.mileage).toLocaleString()} mi`);
  } else if (tab.key === "rvs") {
    if (item.year) subtitleParts.push(item.year);
    if (item.rv_type) subtitleParts.push(item.rv_type);
    if (item.length) subtitleParts.push(`${item.length} ft`);
  } else if (tab.key === "furniture") {
    if (item.brand) subtitleParts.push(item.brand);
    if (item.category) subtitleParts.push(item.category);
    if (item.condition) subtitleParts.push(item.condition);
  }

  return (
    <Link
      to={`/marketplace/${tab.detailPath}/${item.id}`}
      className="group block bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-xl overflow-hidden transition-all duration-300"
      data-testid={`business-inventory-card-${item.id}`}
    >
      <div className="aspect-[4/3] bg-black/40 relative overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={item.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-10 h-10 text-white/20" />
          </div>
        )}
        <div
          className="absolute top-2 left-2 px-2 py-1 rounded-md text-xs font-bold text-white backdrop-blur-md"
          style={{ backgroundColor: `${tab.color}cc` }}
        >
          {tab.label.slice(0, -1) || tab.label}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <h3 className="text-white font-semibold text-sm line-clamp-1">{item.title || "Untitled"}</h3>
          <span className="text-white font-bold text-sm whitespace-nowrap" style={{ color: tab.color }}>
            {formatPrice(item.price)}
          </span>
        </div>
        {subtitleParts.length > 0 && (
          <p className="text-xs text-[#94A3B8] line-clamp-1">{subtitleParts.join(" • ")}</p>
        )}
        {(item.city || item.state) && (
          <p className="text-xs text-[#64748B] mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {[item.city, item.state].filter(Boolean).join(", ")}
          </p>
        )}
      </div>
    </Link>
  );
}

export default function BusinessInventorySection({ business }) {
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!business?.id) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/business/${business.id}/inventory`, { params: { limit: 100 } })
      .then((res) => {
        if (cancelled) return;
        const normalized = (res.data?.items || []).map((it) => ({
          ...it,
          listing_type: TYPE_ALIASES[it.listing_type] || it.listing_type,
        }));
        setItems(normalized);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [business?.id]);

  // Group + figure out which tabs have inventory
  const byType = useMemo(() => {
    const map = {};
    for (const it of items) {
      const key = it.listing_type;
      if (!map[key]) map[key] = [];
      map[key].push(it);
    }
    return map;
  }, [items]);

  const availableTabs = TYPE_TABS.filter((t) => (byType[t.key] || []).length > 0);

  // Auto-select the first tab that has inventory
  useEffect(() => {
    if (!activeTab && availableTabs.length > 0) {
      setActiveTab(availableTabs[0].key);
    }
  }, [availableTabs, activeTab]);

  if (loading) return null;
  if (items.length === 0) return null;

  const currentTab = availableTabs.find((t) => t.key === activeTab) || availableTabs[0];
  const currentItems = byType[currentTab.key] || [];

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6 rounded-2xl"
      data-testid="business-inventory-section"
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold font-['Outfit'] text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-[#00E5FF]" />
            Listings & Inventory
          </h2>
          <p className="text-xs text-[#94A3B8] mt-1">
            {items.length} live {items.length === 1 ? "listing" : "listings"} from {business.name}
          </p>
        </div>
        <Link
          to={`/marketplace?seller=${business.slug || business.id}`}
          className="text-xs text-[#00E5FF] hover:text-white flex items-center gap-1"
          data-testid="business-inventory-view-all"
        >
          View all in marketplace <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {/* "Alert me when this seller lists something new" */}
      <div className="mb-5 flex items-center gap-3 flex-wrap p-3 rounded-lg bg-gradient-to-r from-[#7C3AED]/10 to-[#00E5FF]/10 border border-white/10">
        <div className="flex-1 min-w-[180px]">
          <p className="text-white text-sm font-semibold">Want first dibs on new listings?</p>
          <p className="text-xs text-[#94A3B8]">
            We&apos;ll text or email you the moment {business.name} adds something new.
            <span className="ml-2 px-1.5 py-0.5 rounded-full bg-[#00E5FF]/20 text-[#00E5FF] text-[10px] font-semibold">
              Instant by default
            </span>
          </p>
        </div>
        <SaveSearchButton
          filters={{ listing_type: currentTab.key }}
          businessId={business.id}
          businessName={business.name}
          defaultName={`New ${currentTab.label.toLowerCase()} from ${business.name}`}
          variant="primary"
          testIdPrefix="business-save-search"
        />
      </div>

      {/* Type tabs */}
      <div className="flex flex-wrap gap-2 mb-5" role="tablist">
        {availableTabs.map((t) => {
          const Icon = t.icon;
          const isActive = t.key === currentTab.key;
          const count = (byType[t.key] || []).length;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              role="tab"
              aria-selected={isActive}
              data-testid={`business-inventory-tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                isActive
                  ? "text-white border-transparent"
                  : "bg-white/5 text-[#94A3B8] border-white/10 hover:bg-white/10 hover:text-white"
              }`}
              style={isActive ? { backgroundColor: t.color } : undefined}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${isActive ? "bg-black/20" : "bg-white/10"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Item grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {currentItems.slice(0, 12).map((item) => (
          <ListingCard3D key={item.id} business={item} testid={`inventory-3d-${item.id}`}>
            <ItemCard item={item} tab={currentTab} />
          </ListingCard3D>
        ))}
      </div>

      {currentItems.length > 12 && (
        <div className="mt-5 text-center">
          <Link
            to={`/marketplace?seller=${business.slug || business.id}&type=${currentTab.key}`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm border border-white/10"
            data-testid="business-inventory-show-more"
          >
            Show all {currentItems.length} {currentTab.label.toLowerCase()}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </motion.section>
  );
}
